/**
 * Author: GPT-6 / Codex; existing contributors credited below
 * Date: 2026-10-27
 * PURPOSE: Connect existing public play and reference delivery to approved games and crawlable URLs.
 * SRP/DRY check: Pass — reuses the shared approval feed, mirror/player, content and metadata policy.
 */
/**
 * Author: GPT-6.1 Sol / Codex; Codex
 * Date: 2026-10-08
 * PURPOSE: Keep the competition leaderboard directly visible in the primary navigation,
 *          with named mobile controls and resource/reference links in the existing menus.
 *          Gives the separate ARC Daily newspaper a direct News entry.
 *          Removes the retired model/trustworthiness leaderboard from public navigation.
 *          2026-10-09 (Claude Opus 5.5): archives the early 2025 experiments from navigation:
 *          Elo compare, puzzle DB, test-a-solution, debate, discussion, explanation feedback
 *          and the ML & Kaggle readiness page. Their routes still serve by direct link.
 *          2026-10-22 (Claude Opus 5.5 Low): ARC 1 & 2 menu now leads with puzzle explanations
 *          (puzzle browser, Can You Spot Slop?, explanation ratings) and the kids' emoji game;
 *          drops last year's leftovers (Poetiq, LLM Council, model comparison, trading cards,
 *          LLM reasoning, duplicate resource hub). Their routes still serve by direct link.
 * SRP/DRY check: Pass — maintains one navigation configuration with full-page links where needed.
 */
/**
 * Author: Claude Opus 5; ARC-3 dropdown modernized by Claude Sonnet 5, 2026-09-12
 * Date: 2026-08-29 (ARC-3 dropdown modernized 2026-09-12; Kaggle Leaderboard link added 2026-10-05 by Claude Opus 5.5; All ARC leaderboards link added 2026-10-10 by Claude Sonnet 5.5)
 * PURPOSE: ARC-3-forward top navigation. The primary row is the ARC-AGI-3 flow a visitor
 * actually walks -- Play, Submit, About ARC-3 -- matching the root redirect in
 * App.tsx, which already sends "/" to /arc3/gallery. Everything ARC-1/2 collapses into a single
 * sectioned "ARC 1 & 2" dropdown, and SnakeBench/Worm Arena keeps its own "Arena" dropdown
 * because it belongs to neither generation. The right rail carries the two outbound ARC-3 sites
 * (arcprize.org and Son Pham's sibling catalog at arc3.sonpham.net) plus the repo link.
 * No routes changed -- every archived destination is still registered in App.tsx and still
 * reachable by deep link; this file only decides what gets top billing.
 * 2026-09-12: the ARC-3 dropdown no longer links the agent playground -- this site doesn't
 * run agents against games anymore, so advertising that flow in the nav was stale. Its lead
 * item is now the searchable 25-game spoiler index (/arc3/games, see Arc3GamesIndex.tsx),
 * which previously had no nav entry at all despite being the actual front door for "what
 * are these games."
 * 2026-09-12 PM: also dropped "Hypothesis traces" (/arc3/hypotheses) -- confusing page,
 * not worth a nav slot. Route still exists, just not linked from here.
 * SRP/DRY check: Pass - single responsibility (navigation structure). Reuses shadcn
 * NavigationMenu/DropdownMenu, including the DropdownMenuLabel and DropdownMenuSeparator
 * primitives already exported by components/ui/dropdown-menu.tsx.
 * CRITICAL: dividers render INSIDE each menu item to keep the Radix hierarchy intact.
 * See docs/2026-08-29-arc3-forward-nav-plan.md and
 * docs/plans/2026-09-12-arc3-nav-games-search-plan.md.
 */
import React from 'react';
import { Link, useLocation } from 'wouter';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Archive,
  Brain,
  CircuitBoard,
  Database,
  ExternalLink,
  Gamepad2,
  LayoutGrid,
  Github,
  Grid3X3,
  Info,
  Search,
  Trophy,
  Users,
  Worm,
  Zap,
  BookOpen,
  Newspaper,
} from 'lucide-react';

// Type definitions for discriminated union
interface NavLink {
  /** Keep high-priority destinations readable when other mobile labels collapse. */
  alwaysShowLabel?: boolean;
  /** Load a separate application instead of using the host SPA router. */
  fullPage?: boolean;
  type: 'link';
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
  /**
   * Match the location exactly instead of by prefix. Needed wherever a parent route is a
   * sibling of its own children in the nav: without this, /arc3 ("About ARC-3") highlights
   * on /arc3/gallery too, so two entries look active at once.
   */
  exact?: boolean;
}

/** A labelled run of links inside a dropdown. Keeps the long archive list scannable. */
interface NavSection {
  label: string;
  items: NavLink[];
}

interface NavDropdown {
  type: 'dropdown';
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
  sections: NavSection[];
}

type NavItem = NavLink | NavDropdown;

/** Outbound links. Kept off the main row so leaving the site always looks deliberate. */
interface ExternalNavLink {
  title: string;
  href: string;
  description: string;
}

/**
 * Primary row. ARC-3 first, in use order, then the two collapsed groups.
 *
 * `markerBefore` opens a cluster with its ARC palette square, replacing a single trailing
 * 🟦 that sat after the ARC-3 group and nowhere else. One square used once reads as a
 * stray character; the same square opening every cluster reads as a system, and it is the
 * same palette as the brand mark two inches to its left. Squares are per cluster, never
 * per item -- at eleven top-level entries one on each was noise, which is what the lone
 * survivor was left over from.
 */
const navigationItems: (NavItem & { markerBefore?: string })[] = [
  {
    type: 'link',
    title: 'News',
    href: '/news',
    icon: Newspaper,
    alwaysShowLabel: true,
    description: 'The ARC Daily: morning and evening competition reports',
  },
  {
    type: 'link',
    markerBefore: '🟦',
    title: 'Leaderboard',
    href: '/kaggle-leaderboard',
    icon: Trophy,
    alwaysShowLabel: true,
    description: 'ARC-3 and ARC-2 public competition standings',
  },
  {
    type: 'link',
    title: 'Play',
    // /play, not the gallery. Playing the queue is the one thing this site asks of a
    // visitor, and the gallery is a browsing surface that made them choose first -- three
    // entry points (nav, landing CTA, gallery) each picked a different task, which is how
    // the same visitor could be handed three different "start here"s.
    href: '/play',
    icon: Gamepad2,
    description: 'Play an ARC-AGI-3 task blind -- no title, no rules, no instructions',
  },
  {
    type: 'link',
    title: 'Resources',
    href: '/home',
    icon: Grid3X3,
    description: 'Every ARC-AGI-3 resource in one place: walkthroughs, games, harnesses, papers, data',
  },
  {
    type: 'link',
    title: 'Our Games',
    href: '/arc3/gallery',
    icon: LayoutGrid,
    description: "Team Turducken's synthetic ARC-AGI-3 games, no login required",
  },
  {
    /*
     * WHY THIS BECAME A DROPDOWN. It was a single "About ARC-3" link, and everything else
     * this programme has built had no entry point in the chrome at all: the hypothesis
     * traces (shipped 02-Sep and linked from nowhere), the agent playground, the archive.
     * Pages reachable only by typing the URL are pages nobody visits, and the nav was
     * quietly asserting that the site is three things when it is eight.
     */
    type: 'dropdown',
    title: 'ARC-3',
    icon: BookOpen,
    description: 'Spoilers, reference and research behind the ARC-AGI-3 work',
    sections: [
      {
        label: 'Resources',
        items: [
          {
            type: 'link', title: 'Resource Hub', href: '/home', icon: Grid3X3,
            description: 'Every ARC-AGI-3 resource in one place: walkthroughs, games, harnesses, papers, data',
          },
          {
            type: 'link', title: 'Human & AI Results', href: '/human-records.html', fullPage: true,
            icon: Users, description: 'Published public-game records, scores, action counts and replays',
          },
        ],
      },
      {
        label: 'Spoilers',
        items: [
          {
            /*
             * Lead item on purpose. This is what the dropdown is FOR now -- not the
             * agent playground (removed 2026-09-12, this site doesn't run agents against
             * games anymore) but finding one game's rules fast. Says "spoilers" because
             * it is one: full mechanics for all 25 public-demo games, five of which are
             * playable blind at /arc3/gallery.
             */
            type: 'link',
            title: 'All 25 Games',
            href: '/arc3/games',
            icon: Search,
            description: 'Search every official game by name, ID, or mechanic — full spoilers',
          },
          {
            type: 'link',
            title: 'Slippery Seven',
            href: '/arc3/slippery-seven',
            icon: Search,
            description: 'Seven games from the Qwen 27B research run, with guides and screenshots',
          },
          {
            type: 'link',
            title: 'About ARC-3',
            href: '/arc3',
            icon: BookOpen,
            description: 'Reference and history — the technical report behind the benchmark',
            exact: true,
          },
          {
            type: 'link',
            title: 'Archive',
            href: '/arc3/archive/games',
            icon: Archive,
            description: 'Retired ARC-AGI-3 games and their replays',
          },
        ],
      },
      {
        // Added 2026-10-05 (Claude Opus 5.5): the public Kaggle board, moved off arc-3.
        label: 'Competition',
        items: [
          {
            type: 'link',
            title: 'Kaggle Leaderboard',
            href: '/kaggle-leaderboard',
            icon: Trophy,
            description: 'Every team on the ARC-AGI-3 Kaggle board, medal lines and history',
          },
          {
            type: 'link',
            title: 'All ARC leaderboards',
            href: '/arc-leaderboards',
            icon: Trophy,
            description: 'ARC-AGI-3 and ARC-AGI-2 standings, plus the official ARC Prize board',
          },
        ],
      },
    ],
  },
  {
    type: 'dropdown',
    markerBefore: '🟥🟨',
    title: 'ARC 1 & 2',
    icon: Archive,
    description: 'Puzzle-era analysis, scoring, and datasets',
    sections: [
      {
        // 2026-10-22: puzzle explanations lead again. The 9-Oct archive pass hid them, but
        // they are what people outside the site keep asking for ("what is the rule here?").
        label: 'Puzzle Explanations',
        items: [
          {
            type: 'link',
            title: 'Browse Puzzles',
            href: '/browser',
            icon: Search,
            description: 'Every ARC-1 and ARC-2 puzzle, with AI explanations and human notes',
          },
          {
            type: 'link',
            title: 'Can You Spot Slop?',
            href: '/elo',
            icon: Users,
            description: 'Two AI explanations, one puzzle: pick the better one and spot the confident nonsense',
          },
          {
            type: 'link',
            title: 'Explanation Ratings',
            href: '/explanation-feedback',
            icon: Brain,
            description: 'What people said when they judged AI explanations helpful or not',
          },
        ],
      },
      {
        label: 'Play',
        items: [
          {
            type: 'link', title: 'All ARC-1 & 2 Resources', href: '/arc2', icon: Grid3X3,
            description: 'Puzzle browsers, scoring, results archives and research tools',
          },
          {
            type: 'link', title: 'Human ARC', href: '/human-arc/', fullPage: true,
            icon: Users, description: 'Try ARC puzzles and compare your reasoning with AI',
          },
          {
            type: 'link', title: 'Space Force (for kids)', href: '/human-arc/space-force', fullPage: true,
            icon: Gamepad2, description: 'ARC puzzles with emojis instead of colors, built to show kids',
          },
        ],
      },
      {
        label: 'Datasets & Scoring',
        items: [
          {
            type: 'link',
            title: 'Official Scoring',
            href: '/scoring',
            icon: Zap,
            description: 'Public evaluation set results, two attempts per puzzle',
          },
          {
            type: 'link',
            title: 'ARC-2 Hard Set',
            href: '/arc2/hard-set',
            icon: Database,
            description: 'Eval puzzles no logged attempt has solved, with every model\'s reasoning',
          },
          {
            type: 'link',
            title: 'Analytics',
            href: '/analytics',
            icon: Database,
            description: 'Model performance analytics across the puzzle datasets',
          },
          {
            type: 'link',
            title: 'RE-ARC',
            href: '/re-arc',
            icon: CircuitBoard,
            description: 'Generate unique evaluation datasets and validate submissions',
          },
          {
            type: 'link',
            title: 'Dataset Viewer',
            href: '/dataset-viewer',
            icon: Database,
            description: 'Open or drop any dataset to inspect contents and metadata',
          },
        ],
      },
      {
        label: 'About',
        items: [
          {
            type: 'link',
            title: 'About',
            href: '/about',
            icon: Info,
            description: 'Learn about this project and acknowledgments',
          },
        ],
      },
    ],
  },
  {
    type: 'dropdown',
    markerBefore: '🟩',
    title: 'Arena',
    icon: Worm,
    description: 'SnakeBench and Worm Arena -- LLMs playing snake against each other',
    sections: [
      {
        label: 'SnakeBench',
        items: [
          {
            type: 'link',
            title: 'SnakeBench (Upstream)',
            href: '/snakebench',
            icon: Gamepad2,
            description: 'Official SnakeBench project (upstream)',
          },
        ],
      },
      {
        label: 'Worm Arena',
        items: [
          {
            type: 'link',
            title: 'Replay',
            href: '/worm-arena',
            icon: Worm,
            description: 'Replay a saved match by matchId',
            exact: true,
          },
          {
            type: 'link',
            title: 'Live',
            href: '/worm-arena/live',
            icon: Worm,
            description: 'Run and watch a live match',
          },
          {
            type: 'link',
            title: 'Matches',
            href: '/worm-arena/matches',
            icon: Worm,
            description: 'Browse matches by model (DB-backed)',
          },
          {
            type: 'link',
            title: 'Models',
            href: '/worm-arena/models',
            icon: Worm,
            description: 'Model match history and combat profiles',
          },
          {
            type: 'link',
            title: 'Stats & Placement',
            href: '/worm-arena/stats',
            icon: Worm,
            description: 'Ratings, placements, and leaderboards',
          },
          {
            type: 'link',
            title: 'Skill Analysis',
            href: '/worm-arena/skill-analysis',
            icon: Worm,
            description: 'Model performance analysis and skill metrics',
          },
          {
            type: 'link',
            title: 'Distributions',
            href: '/worm-arena/distributions',
            icon: Worm,
            description: 'Run length distributions and match statistics',
          },
          {
            type: 'link',
            title: 'Rules',
            href: '/worm-arena/rules',
            icon: Worm,
            description: 'Game rules and LLM prompt transparency',
          },
        ],
      },
    ],
  },
];

/**
 * External references retained in the top bar. Community game attribution and licence
 * notices live with the catalog entries; they do not all need primary navigation links.
 *
 * arc3.sonpham.net is Son Pham's sibling catalog, built on the same Pyodide/ARCEngine
 * architecture this repo runs -- see docs/sonpham-arc3-pyodide-architecture.md.
 */
const externalLinks: ExternalNavLink[] = [
  {
    title: 'ARC Prize',
    href: 'https://arcprize.org/arc-agi/3/',
    description: 'Official ARC Prize Foundation site',
  },
  {
    title: 'arc3.sonpham.net',
    href: 'https://arc3.sonpham.net',
    description: "Son Pham's ARC-3 game catalog",
  },
];

export function AppNavigation() {
  const [location] = useLocation();

  const isActiveRoute = (href: string, exact?: boolean) => {
    if (href === '/') {
      return location === '/' || location === '/browser';
    }
    if (exact) {
      return location === href;
    }
    return location.startsWith(href);
  };

  const isDropdownActive = (dropdown: NavDropdown): boolean =>
    dropdown.sections.some(section =>
      section.items.some(item => isActiveRoute(item.href, item.exact)),
    );

  return (
    <div className="flex items-center justify-between w-full gap-2">
      {/* The menu, not the header, is the scroll container. Without min-w-0 the menu refuses
          to shrink and shoves the shrink-0 right rail outside the viewport, which at <=1024px
          hid the two external links and the repo link entirely. Scrolling here instead means
          the ARC-3 row stays flush left and it is the archive dropdowns that scroll away. */}
      <div className="min-w-0 overflow-x-auto">
        {/* Keep the menu at its content width so centered Radix children cannot overflow
            the left edge of this scroll container on narrow phones. */}
        <NavigationMenu className="min-w-max">
        <NavigationMenuList className="flex items-center">
          {navigationItems.map(item => {
            const key = item.type === 'link' ? item.href : item.title;

            return (
              <NavigationMenuItem key={key} className="flex items-center">
                {/* The cluster's palette square, opening the group it belongs to. */}
                {item.markerBefore && (
                  <span className="text-[10px] leading-none mr-1 ml-2 select-none tracking-[-1px]"
                        aria-hidden="true">
                    {item.markerBefore}
                  </span>
                )}
                {/* Dropdowns are secondary by design: lighter weight than the ARC-3 row, so
                    the primary flow reads as primary without resorting to decoration. */}
                {item.type === 'link' ? (
                  <NavigationMenuLink asChild>
                    <Link
                      href={item.href}
                      aria-label={item.title}
                      className={cn(
                        navigationMenuTriggerStyle(),
                        // px-3 overrides the cva's px-4 through twMerge: at px-4 the six
                        // top-level items spend 48px on padding the header needs elsewhere.
                        'flex items-center gap-2 px-3 font-semibold',
                        isActiveRoute(item.href, item.exact) && 'bg-accent text-accent-foreground',
                      )}
                    >
                      <item.icon className="h-4 w-4" aria-hidden="true" />
                      <span className={item.alwaysShowLabel ? 'inline' : 'hidden sm:inline'}>{item.title}</span>
                    </Link>
                  </NavigationMenuLink>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      aria-label={item.title}
                      className={cn(
                        navigationMenuTriggerStyle(),
                        'flex items-center gap-2 px-3 font-normal text-muted-foreground',
                        isDropdownActive(item) && 'bg-accent text-accent-foreground font-medium',
                      )}
                    >
                      <item.icon className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">{item.title}</span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-[280px] p-1">
                      {item.sections.map((section, sectionIndex) => (
                        <React.Fragment key={section.label}>
                          {sectionIndex > 0 && <DropdownMenuSeparator />}
                          <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {section.label}
                          </DropdownMenuLabel>
                          {section.items.map(child => {
                            const isChildActive = isActiveRoute(child.href, child.exact);
                            const ChildLink = child.fullPage ? 'a' : Link;
                            return (
                              <DropdownMenuItem key={child.href} asChild>
                                <ChildLink
                                  href={child.href}
                                  className={cn(
                                    'block select-none rounded-md px-3 py-2 text-sm leading-none no-underline outline-none transition-colors',
                                    'hover:bg-accent hover:text-accent-foreground',
                                    'focus:bg-accent focus:text-accent-foreground',
                                    isChildActive && 'bg-accent text-accent-foreground font-semibold',
                                  )}
                                >
                                  <div className="flex items-center gap-2">
                                    <child.icon className="h-4 w-4 shrink-0" />
                                    <div>
                                      <div className="font-medium">{child.title}</div>
                                      {child.description && (
                                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                                          {child.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </ChildLink>
                              </DropdownMenuItem>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </NavigationMenuItem>
            );
          })}
        </NavigationMenuList>
        </NavigationMenu>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {externalLinks.map(link => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            title={link.description}
            className="hidden md:flex"
          >
            <Button variant="ghost" size="sm" className="flex items-center gap-1.5 font-medium">
              <span className="text-xs">{link.title}</span>
              <ExternalLink className="h-3 w-3 opacity-60" />
            </Button>
          </a>
        ))}
        <a
          href="https://github.com/82deutschmark/arc-explainer"
          target="_blank"
          rel="noopener noreferrer"
          title="Open source on GitHub"
          className="hidden sm:flex"
        >
          <Button variant="ghost" size="sm" className="flex items-center gap-2">
            <Github className="h-4 w-4" />
          </Button>
        </a>
      </div>
    </div>
  );
}
